import { useState, useEffect } from 'react'
import { emailTemplatesApi } from '../services/api'
import {
  findMissingRequiredVariables,
  formatMissingVariablesError,
  getInputType,
  normalizeVariable,
} from '../utils/templateVariableHelpers'
import { extractErrorMessage } from '../utils/errorUtils'

const SendTestModal = ({ template, templateTypeName, onCancel }) => {
  const [toEmail, setToEmail] = useState('')
  const [templateData, setTemplateData] = useState({})
  const [variables, setVariables] = useState([])
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [fillingAI, setFillingAI] = useState(false)

  useEffect(() => {
    loadVariables()
  }, [template])

  const loadVariables = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await emailTemplatesApi.getVariablesByType(template.templateType)
      console.log('Variables response:', response.data)
      
      // Handle different response formats
      let variableList = []
      if (response.data && Array.isArray(response.data.variables)) {
        // Primary format: {templateType: 0, variables: [{name, isRequired, description}, ...]}
        variableList = response.data.variables
      } else if (Array.isArray(response.data)) {
        variableList = response.data
      } else if (response.data && typeof response.data === 'object') {
        // If it's an object, get the values or convert to array
        variableList = Object.values(response.data).flat()
      }
      
      setVariables(variableList)
      
      // Initialize templateData with empty values for each variable
      const initialData = {}
      variableList.forEach(variable => {
        // Handle both string variables and object variables with name property
        const variableName = typeof variable === 'string' ? variable : variable.name
        initialData[variableName] = ''
      })
      setTemplateData(initialData)
      
    } catch (err) {
      console.error('Failed to load variables:', err)
      setError('Failed to load template variables')
      setVariables([])
    } finally {
      setLoading(false)
    }
  }

  const handleVariableChange = (variableName, value) => {
    setTemplateData(prev => ({
      ...prev,
      [variableName]: value
    }))
  }

  const handleSendTest = async () => {
    if (!toEmail.trim()) {
      setError('Please enter a recipient email address')
      return
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(toEmail)) {
      setError('Please enter a valid email address')
      return
    }

    const missingVariables = findMissingRequiredVariables(variables, templateData)
    if (missingVariables.length > 0) {
      setError(formatMissingVariablesError(missingVariables))
      return
    }

    try {
      setSending(true)
      setError(null)

      const emailRequest = {
        templateType: template.templateType,
        toEmail: toEmail,
        templateData: templateData
      }

      console.log('Sending test email:', emailRequest)
      await emailTemplatesApi.send(emailRequest)
      
      setSuccess(true)
      setTimeout(() => {
        onCancel()
      }, 2000)
      
    } catch (err) {
      console.error('Failed to send test email:', err)
      setError(err.response?.data?.detail || err.message || 'Failed to send test email')
    } finally {
      setSending(false)
    }
  }

  const handleAutoFillAI = async () => {
    try {
      setFillingAI(true)
      setError(null)
      const response = await emailTemplatesApi.generateTestData(template.templateType)
      if (response.data && typeof response.data === 'object') {
        setTemplateData(prev => ({ ...prev, ...response.data }))
      }
    } catch (err) {
      setError(extractErrorMessage(err))
    } finally {
      setFillingAI(false)
    }
  }

  if (success) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
          <div className="text-center">
            <div className="text-green-500 text-4xl mb-4">✅</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Test Email Sent!</h3>
            <p className="text-gray-600">The test email has been sent successfully to {toEmail}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Send Test Email - {templateTypeName}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="text-gray-500">Loading template variables...</div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Recipient Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recipient Email
              </label>
              <input
                type="email"
                value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter recipient email address"
                required
              />
            </div>

            {/* Template Variables */}
            {variables.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Template Variables
                  </label>
                  <button
                    type="button"
                    onClick={handleAutoFillAI}
                    disabled={fillingAI}
                    className="inline-flex items-center px-3 py-1 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-full hover:bg-purple-100 disabled:opacity-50 transition-colors"
                  >
                    {fillingAI ? (
                      <>
                        <div className="w-3 h-3 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mr-1.5" />
                        Filling...
                      </>
                    ) : (
                      <>
                        <svg className="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        Auto-fill with AI
                      </>
                    )}
                  </button>
                </div>
                <div className="space-y-3">
                  {variables.map((variable, index) => {
                    const { name: variableName, description: variableDescription, isRequired, type: variableType } = normalizeVariable(variable)
                    const inputType = getInputType(variableType)

                    return (
                      <div key={`${variableName}-${index}`}>
                        <label className="block text-xs text-gray-600 mb-1">
                          {variableName} {isRequired && <span className="text-red-500">*</span>}
                          {variableDescription && (
                            <span className="text-gray-500 font-normal ml-1">
                              - {variableDescription}
                            </span>
                          )}
                        </label>
                        <input
                          type={inputType}
                          value={templateData[variableName] || ''}
                          onChange={(e) => handleVariableChange(variableName, e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder={`Enter value for ${variableName}`}
                          required={isRequired}
                        />
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {variables.length === 0 && !loading && (
              <div className="text-gray-500 text-sm">
                No variables required for this template type.
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={onCancel}
                disabled={sending}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSendTest}
                disabled={sending || !toEmail.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? 'Sending...' : 'Send Test Email'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default SendTestModal